import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AlertClaim, MessageAlert, getAlertBus } from './alertBus';
import { ConversationAvatar } from './ConversationList';
import { markDone, takeLead } from './inboxApi';
import { ToastEntry, ToastStore, stackView } from './toastStore';
import { alertCopy } from './types';

/**
 * Message toasts (plan §5, T-076): a stack of their own, bottom-right just
 * left of the inbox, apart from react-admin's "Element updated" snackbar in
 * the middle. They stay until dealt with — ✕, Clear all, opening the
 * conversation, or somebody handling it elsewhere — with the newest three
 * shown and the rest behind a "+N more" pill. A new message from the same
 * conversation merges into its toast ("3 new messages").
 */

export const toastStore = new ToastStore();

/** Set by the tab each time it shows a toast; the buttons need it. */
let actionsNow: ToastActions | null = null;

export interface ToastActions {
  me: { id: string; name: string | null };
  onOpen: (alert: MessageAlert) => void;
}

// ─── Showing, updating, clearing ─────────────────────────────────────────────

export function showMessageToast(alert: MessageAlert, actions: ToastActions) {
  actionsNow = actions;
  toastStore.show(alert);
  if (alert.sound) playChime();
}

export function claimLine(claim: AlertClaim, meId: string | undefined): string {
  const who = claim.id === meId ? 'You' : claim.name || 'Someone';
  if (claim.source === 'manual') return claim.id === meId ? 'Assigned to you' : `Assigned to ${who}`;
  return `${who} took it`;
}

/**
 * A lead on an unclaimed toast got an owner. Somebody else: "Sylvie took it",
 * then the toast goes. Me (assigned to me meanwhile): it's mine now, and stays.
 */
export function updateToastClaim(key: string, claim: AlertClaim) {
  const entry = toastStore.get(key);
  if (!entry) return;
  toastStore.claim(key, claim);
  const meId = actionsNow?.me.id;
  if (entry.alert.kind === 'unclaimed' && claim.id !== meId) {
    toastStore.handle(key, claimLine(claim, meId));
  }
}

/** Handled elsewhere (a reply, Done, a snooze): say so, then go. This tab only. */
export function clearToast(key: string, reason?: string) {
  toastStore.handle(key, reason || 'Handled');
}

/** The user closed it here (✕, Open, Done, or opened the thread): every tab. */
export function dismissToast(key: string) {
  toastStore.remove(key);
  getAlertBus().broadcast({ type: 'toast-closed', key });
}

/** Clear all: every toast in every tab, hidden ones included. */
export function clearAllToasts() {
  toastStore.removeAll();
  getAlertBus().broadcast({ type: 'toasts-cleared' });
}

// ─── Sound ───────────────────────────────────────────────────────────────────

let audio: AudioContext | null = null;

/** A soft two-note chime, synthesised so there's no asset to ship. */
export function playChime() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    audio = audio ?? new Ctx();
    const ctx = audio;
    [880, 1318.5].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.12;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
  } catch {
    /* no audio — the toast still shows */
  }
}

// ─── Time ────────────────────────────────────────────────────────────────────

/** "now", "4 min", "2 h" — the toasts can sit there a while now. */
export function shortAgo(iso: string, now = Date.now()): string {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return 'now';
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)} h`;
}

function firstLine(iso: string, now: number): string {
  const ago = shortAgo(iso, now);
  return ago === 'now' ? 'first just now' : `first ${ago} ago`;
}

/** Re-render every 30 s so the times move on. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

// ─── The stack ───────────────────────────────────────────────────────────────

/**
 * Rendered once, by InboxDockProvider. At rest the newest toast sits in front
 * with the next two peeking behind it; hovering (or tabbing into) the stack
 * fans them out (index.css `.message-toasts`).
 */
export function MessageToastStack({ onMore }: { onMore: () => void }) {
  const entries = useSyncExternalStore(toastStore.subscribe, toastStore.getSnapshot);
  const now = useNow();
  if (!entries.length) return null;

  const { shown, more, live } = stackView(entries);
  const actions = actionsNow;

  return createPortal(
    <div
      className="cleo-ui message-toasts"
      data-testid="message-toasts"
      style={{ '--peek': `${Math.max(0, Math.min(shown.length, 3) - 1) * 10}px` } as React.CSSProperties}
    >
      {live >= 2 && (
        <div className="flex items-center gap-2">
          {more > 0 && (
            <button
              type="button"
              onClick={onMore}
              data-testid="toasts-more"
              className="rounded-full border border-border bg-background px-3 py-0.5 text-xs font-bold text-foreground shadow-sm hover:bg-muted"
            >
              +{more} more
            </button>
          )}
          <button
            type="button"
            onClick={clearAllToasts}
            data-testid="toasts-clear-all"
            className="ml-auto rounded-full bg-foreground/80 px-3 py-1 text-xs font-semibold text-background hover:bg-foreground"
          >
            {more > 0 ? `Clear all ${live}` : 'Clear all'}
          </button>
        </div>
      )}
      <div className="message-toasts-list">
        {actions &&
          shown.map((e) => <MessageToast key={e.alert.key} entry={e} actions={actions} now={now} />)}
      </div>
    </div>,
    document.body,
  );
}

// ─── The toast ───────────────────────────────────────────────────────────────

function ActionButton({
  onClick,
  primary,
  disabled,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50',
        primary
          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
          : 'border border-border bg-background text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

function MessageToast({
  entry,
  actions,
  now,
}: {
  entry: ToastEntry;
  actions: ToastActions;
  now: number;
}) {
  const { alert, handled, leaving } = entry;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { item } = alert;
  const { title, context, preview } = alertCopy(item);
  const unclaimed = alert.kind === 'unclaimed';
  const canTake = unclaimed && !alert.claim && !!item.lead_id;

  const open = () => {
    actions.onOpen(alert);
    dismissToast(alert.key);
  };

  const done = async () => {
    setBusy(true);
    try {
      await markDone(item.conversation_key, actions.me);
      dismissToast(alert.key);
    } catch (e: any) {
      setError(e?.message || 'Could not mark it done');
    } finally {
      setBusy(false);
    }
  };

  const take = async () => {
    if (!item.lead_id) return;
    setBusy(true);
    try {
      const won = await takeLead(item.lead_id, actions.me.id);
      if (won) {
        // Tell every tab of this browser at once; other browsers hear it from
        // realtime (the leads UPDATE) and change to "… took it". Then open it,
        // which takes this toast down everywhere.
        getAlertBus().broadcast({
          type: 'alert-update',
          key: alert.key,
          claim: { id: actions.me.id, name: actions.me.name, source: 'claim' },
        });
        open();
      }
      // Lost the race: the leader's leads subscription names the winner, and
      // the toast says so on its way out.
    } catch (e: any) {
      setError(e?.message || 'Could not take it');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'message-toast flex w-[356px] max-w-full gap-3 rounded-xl border bg-background p-3 shadow-lg',
        handled ? 'border-primary/40' : 'border-border',
      )}
      data-testid="message-toast"
      data-conversation-key={alert.key}
      data-leaving={leaving || undefined}
    >
      <ConversationAvatar item={item} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-bold text-foreground">{title}</span>
          {unclaimed && !alert.claim && (
            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">
              Unclaimed
            </span>
          )}
          {alert.count > 1 && (
            <span
              className="shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground"
              data-testid="toast-count"
            >
              {alert.count} new
            </span>
          )}
          <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {shortAgo(item.last_message_at, now)}
          </span>
          <button
            type="button"
            onClick={() => dismissToast(alert.key)}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Dismiss"
            data-testid="toast-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {context && (
          <span className="truncate text-xs font-medium text-muted-foreground">{context}</span>
        )}
        <span className="line-clamp-2 text-sm text-foreground">{preview}</span>
        {alert.count > 1 && (
          <span className="text-xs text-muted-foreground">
            {alert.count} new messages · {firstLine(alert.firstAt, now)}
          </span>
        )}
        {alert.claim && !handled && (
          <span className="text-xs font-semibold text-violet-700" data-testid="toast-claim">
            {claimLine(alert.claim, actions.me.id)}
          </span>
        )}
        {error && <span className="text-xs text-destructive">{error}</span>}
        {handled ? (
          <div
            className="mt-1.5 flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary"
            data-testid="toast-handled"
          >
            <Check className="h-3.5 w-3.5" />
            {handled}
          </div>
        ) : (
          <div className="mt-1.5 flex gap-1.5">
            <ActionButton primary onClick={open}>
              Open
            </ActionButton>
            {canTake && (
              <ActionButton disabled={busy} onClick={() => void take()}>
                Take it
              </ActionButton>
            )}
            <ActionButton disabled={busy} onClick={() => void done()}>
              Done
            </ActionButton>
          </div>
        )}
      </div>
    </div>
  );
}
